/**
 * ChoiceReadIntentEditor  (DA-2184)
 *
 * The inline replacement for the old MapperBuilderDialog. Authors ONLY the per-choice READ
 * intent as structured clauses (role 'READ'); the backend now DERIVES write + unselect +
 * stateMapper from that read intent on save (BE-authoritative, save-only). There is no more
 * WRITE/UNSELECT authoring in the UI, no live serialization of those strings, and no modal —
 * it renders directly inside each choice card.
 *
 * Write-back goes through the caller's `onChange(updatedChoice)`, which threads into the same
 * handleChoiceChange / onChoicesChange path every other choice edit uses, so diff + save stay
 * untouched. We only ever set `hpMapperClauses`/`apMapperClauses` (filtered to READ) and
 * `mapperSource` — never readMapper/writeMapper/unselectMapper (the BE owns those now).
 *
 * A question-level `scope` (QuestionScopeSelector) pins each clause's TARGET; the "Add read
 * condition" seed and the clause rows both honour it. Operator + value stay free per choice.
 */
import React, { useRef } from 'react'
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material'
import { Plus } from 'lucide-react'
import { useGlobalConfig } from 'context/GlobalContext'
import { AnswerChoice, ApMapperClause, HpMapperClause, MapperCatalog, MapperSource } from 'types/apis/surveyInterfaces'
import { useMapperCatalog } from './useMapperCatalog'
import { HpClauseRow } from './HpClauseRow'
import { ApClauseRow } from './ApClauseRow'
import { MapperScopeConstraint } from './cascadeTypes'

export interface ChoiceReadIntentEditorProps {
  choice: AnswerChoice
  profileCode: 'HP' | 'AP'
  questionType: string
  scope?: MapperScopeConstraint
  catalog?: MapperCatalog
  loading?: boolean
  /** Catalog fetch failure from the parent; preferred over this editor's own hook error. */
  error?: string
  disabled?: boolean
  onChange: (updated: AnswerChoice) => void
}

// SM-25 carry-over: writing `[]` where the choice had `undefined` (nothing) is not a real
// change, but isEqual sees one — so a legacy row would light up as modified just from adding
// then removing a clause. Preserve the original shape when there was nothing before and
// nothing now.
const keepShape = <T,>(next: T[], original?: T[]): T[] | undefined =>
  next.length === 0 && !original?.length ? original : next

export const ChoiceReadIntentEditor: React.FC<ChoiceReadIntentEditorProps> = ({
  choice,
  profileCode,
  scope,
  catalog: catalogProp,
  loading: loadingProp,
  error: errorProp,
  disabled = false,
  onChange,
}) => {
  const { rc } = useGlobalConfig()
  // Module-cached — one catalog fetch per form session even with N editors mounted (DA-2293).
  // A parent may still pass the catalog in to skip the hook's state entirely.
  const hook = useMapperCatalog(rc)
  const catalog = catalogProp ?? hook.catalog
  const loading = loadingProp ?? hook.loading
  // Prefer the parent's error (it owns the fetch when it passes the catalog in); fall back to
  // this editor's own hook error only when it is self-fetching.
  const error = errorProp ?? (catalogProp ? undefined : hook.error)

  // A successful fetch that carries no targets for this profile is a dead end: "Add read
  // condition" would seed a clause whose pickers have nothing to choose. Say so and block it.
  const noTargets = !loading && !error && (
    profileCode === 'HP' ? !catalog?.homeProfileAttributes?.length : !catalog?.appliances?.length
  )

  // The choice's clauses + mapperSource as loaded, captured once. The keepShape guard compares
  // against the true original (not a value this editor already wrote back), and mapperSource
  // reverts to the original when all structured content is removed — so add-then-remove doesn't
  // strand a legacy MANUAL/undefined row as AUTO.
  const originalRef = useRef<{ hp?: HpMapperClause[]; ap?: ApMapperClause[]; mapperSource?: MapperSource } | undefined>(undefined)
  if (!originalRef.current) {
    originalRef.current = { hp: choice.hpMapperClauses, ap: choice.apMapperClauses, mapperSource: choice.mapperSource }
  }

  // Manage READ clauses only. Any legacy WRITE/UNSELECT clauses are ignored here and dropped
  // on the first edit — the BE re-derives write/unselect from the read intent on save.
  const readHp = (choice.hpMapperClauses ?? []).filter((c) => c.role === 'READ')
  const readAp = (choice.apMapperClauses ?? []).filter((c) => c.role === 'READ')

  const emit = (nextHp: HpMapperClause[], nextAp: ApMapperClause[]) => {
    const hp = keepShape(nextHp, originalRef.current?.hp)
    const ap = keepShape(nextAp, originalRef.current?.ap)
    const hasStructured = !!hp?.length || !!ap?.length
    onChange({
      ...choice,
      // Only claim AUTO once there is structured content to derive from — an untouched
      // MANUAL/legacy row is not silently converted, and removing everything reverts to
      // whatever the row was loaded as.
      mapperSource: hasStructured ? 'AUTO' : originalRef.current?.mapperSource,
      hpMapperClauses: hp,
      apMapperClauses: ap,
      // Deliberately NOT setting readMapper/writeMapper/unselectMapper — the BE derives them.
    })
  }

  const addClause = () => {
    if (profileCode === 'HP') {
      emit([...readHp, { role: 'READ', attributePath: scope?.attributePath ?? '' }], readAp)
    } else {
      // Seed with the full scoped appliance set; >1 defaults to an OR (`|`) group. The author
      // can trim the set / switch the combinator in the clause row.
      const seedIds = scope?.applianceIds ? [...scope.applianceIds] : []
      emit(readHp, [
        ...readAp,
        {
          role: 'READ',
          applianceIds: seedIds,
          ...(seedIds.length > 1 ? { groupOperator: 'OR' as const } : {}),
          operator: 'C',
        },
      ])
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        Read conditions (write / unselect derived on save)
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }} role="status">
          <CircularProgress size={20} aria-label="Loading mapper catalog" />
        </Box>
      ) : noTargets ? (
        <Typography variant="body2" color="text.secondary">
          {profileCode === 'HP'
            ? 'No home-profile attributes available for this account type.'
            : 'No appliances available for this account type.'}
        </Typography>
      ) : (
        <>
          {readHp.length === 0 && readAp.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No read conditions yet.
            </Typography>
          )}

          {profileCode === 'HP'
            ? readHp.map((c, index) => (
                <HpClauseRow
                  key={index}
                  clause={c}
                  role="READ"
                  catalog={catalog}
                  loading={loading}
                  scope={scope}
                  onChange={(updated) => {
                    const next = [...readHp]
                    next[index] = updated
                    emit(next, readAp)
                  }}
                  onRemove={() => emit(readHp.filter((_, i) => i !== index), readAp)}
                />
              ))
            : readAp.map((c, index) => (
                <ApClauseRow
                  key={index}
                  clause={c}
                  role="READ"
                  catalog={catalog}
                  loading={loading}
                  scope={scope}
                  onChange={(updated) => {
                    const next = [...readAp]
                    next[index] = updated
                    emit(readHp, next)
                  }}
                  onRemove={() => emit(readHp, readAp.filter((_, i) => i !== index))}
                />
              ))}

          {!disabled && (
            <Button
              startIcon={<Plus size={16} />}
              onClick={addClause}
              variant="outlined"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              Add read condition
            </Button>
          )}
          {/* DA-2184: the derived read/write/unselect strings are no longer previewed here —
              they surface read-only in the per-choice info hover (AnswerChoicesSection). */}
        </>
      )}
    </Box>
  )
}
